import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "../../../utils/classnames";
import {
    POSTAL_CODE_STATUS,
    PostalCodeStatusIcon,
    PostalCodeStatusMessage,
    usePostalCodeLookup,
} from "../../input/postal-code";

/**
 * Postal code field for admin forms, with the same live check as the front
 * pages: while the admin types it checks the code (spinner), then draws a
 * check mark and hands back the city — or says the code does not exist.
 *
 * The check only runs once the admin has typed in this field. Opening a form
 * for an existing client must not replace the city they saved with the one
 * the geocoder guesses. Give the component a new `key` each time its form
 * opens, so that "has typed" starts from false again.
 *
 * onCityFound(city)       – the code exists; fill the city with it.
 * onPostalCodeNotFound()  – the code does not exist; clear the city.
 * onStatusChange(status)  – every status change (see POSTAL_CODE_STATUS), so
 *                           the form can hold its Save while a check runs.
 */
const AdminPostalCodeInput = ({
    name = "postalCode",
    placeholder = "Postal Code",
    value,
    onChange,
    countryCode,
    disabled = false,
    onCityFound,
    onPostalCodeNotFound,
    onStatusChange,
    mb = "mb-3",
}) => {
    const [hasAdminTyped, setHasAdminTyped] = useState(false);

    const postalCodeLookup = usePostalCodeLookup({
        postalCode: value?.trim(),
        countryCode,
        // "-" is the placeholder value for countries without postal codes.
        isEnabled: hasAdminTyped && !disabled && value !== "-",
        onFound: onCityFound,
        onNotFound: onPostalCodeNotFound,
    });
    const { status } = postalCodeLookup;

    useEffect(() => {
        onStatusChange?.(status);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    const handleChange = (event) => {
        setHasAdminTyped(true);
        onChange(event);
    };

    const isNotFound = status === POSTAL_CODE_STATUS.NOT_FOUND;

    return (
        <div className={mb}>
            <div
                className={cn(
                    "tw:relative tw:flex tw:h-[50px] tw:items-center tw:rounded tw:border tw:bg-white tw:transition-[border-color,box-shadow] tw:duration-200",
                    "tw:focus-within:border-[#DBBE78] tw:focus-within:shadow-[0_0_0_3px_rgba(219,190,120,0.18)]",
                    isNotFound ? "tw:border-[#E5484D]" : "tw:border-[#DEE2E6]",
                    disabled && "tw:bg-[#F9F9F9]"
                )}
            >
                <input
                    type="text"
                    name={name}
                    value={value ?? ""}
                    placeholder={placeholder}
                    disabled={disabled}
                    onChange={handleChange}
                    autoComplete="postal-code"
                    aria-invalid={isNotFound}
                    className="tw:h-full tw:w-full tw:rounded tw:border-0 tw:bg-transparent tw:!pl-3 tw:!pr-10 tw:!text-[15px] tw:font-['Hanken_Grotesk'] tw:text-black tw:outline-none tw:placeholder:!text-[15px]"
                />
                <span className="tw:pointer-events-none tw:absolute tw:right-3 tw:top-1/2 tw:-translate-y-1/2">
                    <PostalCodeStatusIcon status={status} />
                </span>
            </div>

            <PostalCodeStatusMessage status={status} onRetry={postalCodeLookup.retry} lng="en" />

            <AnimatePresence initial={false}>
                {isNotFound && (
                    <motion.p
                        key="not-found"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="text-danger tw:!m-0 tw:overflow-hidden tw:pt-1.5 tw:text-[12px]"
                        role="alert"
                    >
                        This postal code was not found. Please check it.
                    </motion.p>
                )}
            </AnimatePresence>
        </div>
    );
};

export default AdminPostalCodeInput;
